import { Container, Graphics, Text } from 'pixi.js';
import { CANVAS_FONT } from '../../fonts';
import { TILE } from '../../map/world';

/**
 * Combat effects (§7) — real, visible projectiles + swings wired to the damage
 * system, so nothing deals (or takes) damage silently:
 *   - arrows: a projectile flies from shooter to the target's position; damage is
 *     applied on ARRIVAL (via the onHit callback), not on fire. Misses fly on and
 *     despawn at max range.
 *   - swing arcs: a slash in the attacker's facing direction on a melee hit.
 *   - hit sparks + floating damage numbers on connect.
 *
 * Art placeholder: the pack has no arrow/slash/spark sprites, so these are drawn
 * with Graphics primitives (logged in TODO_ASSETS). The behaviour + timing are the
 * point; the sprites can be swapped in later.
 */

type Arrow = { g: Graphics; x: number; y: number; vx: number; vy: number; tx: number; ty: number; left: number; onHit: () => void; hitR: number; done: boolean };
type Fx = { obj: Container; t: number; life: number; kind: 'spark' | 'slash' | 'dmg'; vy?: number };

const ARROW_SPEED = 11 * TILE;    // px/sec
const HIT_RADIUS = TILE * 0.55;

export interface Effects {
  layer: Container;
  /** Fire an arrow from (fx,fy) toward (tx,ty); onHit fires when it reaches the target. */
  arrow(fx: number, fy: number, tx: number, ty: number, onHit: () => void): void;
  /** A slash arc at (x,y) facing dir ('down'|'up'|'left'|'right'). */
  slash(x: number, y: number, dir: string, color?: number): void;
  /** A hit spark + a floating damage number at (x,y). */
  hit(x: number, y: number, amount: number, color?: number): void;
  /**
   * A floating "-1 ⚡" over the hero, with no spark and no damage framing.
   *
   * Separate from `hit` because spending energy is not being hurt: it drifts
   * instead of popping, it is the energy colour, and it must never fire the red
   * spark that means "you took a hit".
   */
  cost(x: number, y: number, amount: number): void;
  update(dt: number): void;
  destroy(): void;
}

export function createEffects(): Effects {
  const layer = new Container();
  // Floating damage numbers sort ABOVE everything (zIndex 1e5+) and would steal
  // taps for the second or so they are alive.
  layer.eventMode = 'none';
  layer.sortableChildren = true;
  const arrows: Arrow[] = [];
  const fx: Fx[] = [];

  const spark = (x: number, y: number, color: number) => {
    const g = new Graphics();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      g.moveTo(0, 0).lineTo(Math.cos(a) * 8, Math.sin(a) * 8);
    }
    g.stroke({ color, width: 2, alpha: 0.9 });
    g.x = x; g.y = y; g.zIndex = y + 1e5;
    layer.addChild(g);
    fx.push({ obj: g, t: 0, life: 0.25, kind: 'spark' });
  };

  return {
    layer,
    arrow(fx0, fy0, tx, ty, onHit) {
      const dx = tx - fx0, dy = ty - fy0;
      const d = Math.hypot(dx, dy) || 1;
      const g = new Graphics();
      g.moveTo(-9, 0).lineTo(9, 0).stroke({ color: 0xe8e0c0, width: 2 });
      g.moveTo(9, 0).lineTo(4, -3).moveTo(9, 0).lineTo(4, 3).stroke({ color: 0xe8e0c0, width: 2 });   // arrowhead
      g.rotation = Math.atan2(dy, dx);
      g.x = fx0; g.y = fy0; g.zIndex = fy0 + 1e5;
      layer.addChild(g);
      arrows.push({ g, x: fx0, y: fy0, vx: (dx / d) * ARROW_SPEED, vy: (dy / d) * ARROW_SPEED, tx, ty, left: 12 * TILE, onHit, hitR: HIT_RADIUS, done: false });
    },
    slash(x, y, dir, color = 0xffffff) {
      const g = new Graphics();
      const base = dir === 'left' ? Math.PI : dir === 'right' ? 0 : dir === 'up' ? -Math.PI / 2 : Math.PI / 2;
      g.arc(0, 0, TILE * 0.7, base - 0.7, base + 0.7).stroke({ color, width: 4, alpha: 0.85 });
      g.x = x; g.y = y - TILE * 0.5; g.zIndex = y + 1e5;
      layer.addChild(g);
      fx.push({ obj: g, t: 0, life: 0.18, kind: 'slash' });
    },
    hit(x, y, amount, color = 0xff6666) {
      spark(x, y, color);
      const t = new Text({ text: `-${amount}`, style: { fontFamily: CANVAS_FONT, fontSize: 14, fill: color, fontWeight: 'bold', stroke: { color: 0x000000, width: 3 } } });
      t.anchor.set(0.5, 1); t.x = x; t.y = y - TILE * 0.6; t.zIndex = y + 2e5;
      layer.addChild(t);
      fx.push({ obj: t, t: 0, life: 0.8, kind: 'dmg', vy: -30 });
    },
    cost(x, y, amount) {
      // Rendered a little left of centre and higher than a damage number, so a
      // chop that both costs energy and drops loot doesn't stack two numbers on
      // the same pixel. Lives longer and rises slower than a hit: the player is
      // meant to read it, not just register a flash.
      const t = new Text({
        text: `-${amount}`,
        style: { fontFamily: CANVAS_FONT, fontSize: 13, fill: 0xffd35e, fontWeight: 'bold', stroke: { color: 0x2a1c08, width: 3 } },
      });
      t.anchor.set(0.5, 1);
      t.x = x; t.y = y - TILE * 0.95; t.zIndex = y + 2e5;
      layer.addChild(t);
      fx.push({ obj: t, t: 0, life: 1.1, kind: 'dmg', vy: -22 });
    },
    update(dt) {
      for (let i = arrows.length - 1; i >= 0; i--) {
        const a = arrows[i];
        const step = ARROW_SPEED * dt;
        a.x += a.vx * dt; a.y += a.vy * dt; a.left -= step;
        a.g.x = a.x; a.g.y = a.y;
        if (!a.done && Math.hypot(a.x - a.tx, a.y - a.ty) <= a.hitR) { a.done = true; a.onHit(); spark(a.tx, a.ty, 0xff8866); }
        if (a.done || a.left <= 0) { a.g.destroy(); arrows.splice(i, 1); }
      }
      for (let i = fx.length - 1; i >= 0; i--) {
        const f = fx[i];
        f.t += dt;
        const k = f.t / f.life;
        f.obj.alpha = Math.max(0, 1 - k);
        if (f.kind === 'spark' || f.kind === 'slash') f.obj.scale.set(1 + k * 0.8);
        if (f.kind === 'dmg' && f.vy) f.obj.y += f.vy * dt;
        if (f.t >= f.life) { f.obj.destroy(); fx.splice(i, 1); }
      }
    },
    destroy() { layer.destroy({ children: true }); },
  };
}
